import { filterBy } from "@progress/kendo-data-query";
import {
  MultiSelect,
  MultiSelectFilterChangeEvent,
  MultiSelectProps,
} from "@progress/kendo-react-dropdowns";
import { FieldWrapper } from "@progress/kendo-react-form";
import { Error, Hint } from "@progress/kendo-react-labels";
import isString from "lodash-es/isString";
import * as React from "react";
import { useCallback } from "react";
import useDeepCompareEffect from "use-deep-compare-effect";
import type { SelectData } from "./FormDropDownList";
import FormLabel from "./FormLabel";
import FormSelectItemRender from "./FormSelectItemRender";
import IconHint from "./IconHint";
import ListNoData from "./ListNoData";
import { useControlledField, type FieldValidator } from "./useControlledField";

type ExtraProps = Omit<
  MultiSelectProps,
  "name" | "value" | "onChange" | "valid" | "data" | "ref"
>;

export interface FormKendoMultiSelectProps extends ExtraProps {
  name: string;
  validator?: FieldValidator;
  label?: string;
  hint?: string;
  hintIcon?: boolean;
  optional?: boolean;
  showErrorOnPristine?: boolean;
  floatingLabel?: boolean;
  floatLabel?: string;
  data?: SelectData[];
  noDataMessage?: string;
  popupWidth?: string;
  /** Separator that triggers split-on-paste; combined with allowCustom */
  valueSeparator?: string;
  itemRender?: MultiSelectProps["itemRender"];
  listNoDataRender?: MultiSelectProps["listNoDataRender"];
}

export const FormKendoMultiSelect: React.FC<FormKendoMultiSelectProps> = ({
  name,
  validator,
  label,
  hint,
  hintIcon = false,
  optional,
  showErrorOnPristine = false,
  floatingLabel,
  floatLabel,
  disabled,
  placeholder,
  data = [],
  noDataMessage,
  popupWidth,
  valueSeparator,
  itemRender,
  listNoDataRender,
  textField,
  dataItemKey,
  loading,
  allowCustom,
  ...others
}) => {
  const { field, fieldState } = useControlledField({ name, validator });
  const showError =
    !!fieldState.error && (showErrorOnPristine || fieldState.isTouched);
  const showHint = !!hint;
  const inputId = name;
  const labelId = label ? `${inputId}_label` : "";
  const hintId = showHint ? `${inputId}_hint` : "";
  const errorId = showError ? `${inputId}_error` : "";

  const normalizeValue = useCallback(
    (inputValue: unknown): SelectData[] => {
      let v = inputValue;
      while (
        v &&
        typeof v === "object" &&
        !Array.isArray(v) &&
        "value" in (v as Record<string, unknown>)
      ) {
        v = (v as { value?: unknown }).value;
      }
      if (Array.isArray(v)) return v as SelectData[];
      if (!isString(v)) return [];
      const parsed = valueSeparator ? v.split(valueSeparator) : [v];
      return parsed
        .map(item => item.trim())
        .filter(Boolean)
        .map(itemName => ({ name: itemName }));
    },
    [valueSeparator],
  );

  const value = normalizeValue(field.value);
  const [dropDownData, setDropDownData] = React.useState<SelectData[]>([]);
  const [filterValue, setFilterValue] = React.useState<string>("");

  useDeepCompareEffect(() => {
    setDropDownData(data);
  }, [data]);

  const filterChange = (event: MultiSelectFilterChangeEvent) => {
    let next = event.filter.value || "";
    if (valueSeparator && allowCustom && next.includes(valueSeparator)) {
      const parts: string[] = next.split(valueSeparator);
      const lastPart = (parts.pop() || "").trim();
      next = lastPart;
      const additions: SelectData[] = parts
        .filter((p: string) => p.trim().length)
        .map((itemName: string) => ({ name: itemName.trim() }));
      field.onChange([...value, ...additions]);
    }
    setFilterValue(next.trim());
    setDropDownData(filterBy([...data], event.filter));
  };

  return (
    <FieldWrapper>
      <FormLabel
        id={labelId}
        editorId={inputId}
        editorValid={!fieldState.error}
        editorDisabled={!!disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        value={value.length > 0 ? value.map(v => v.name).join(",") : undefined}
        placeholder={placeholder}
        optional={optional}
      >
        <MultiSelect
          {...others}
          name={name}
          ariaDescribedBy={`${hintId} ${errorId}`}
          ariaLabelledBy={labelId}
          valid={!fieldState.error}
          disabled={disabled}
          textField={textField ?? "name"}
          dataItemKey={dataItemKey ?? "id"}
          filterable={true}
          itemRender={itemRender || FormSelectItemRender}
          onFilterChange={filterChange}
          filter={filterValue}
          data={dropDownData}
          placeholder={placeholder}
          popupSettings={popupWidth ? { width: popupWidth } : {}}
          listNoDataRender={
            listNoDataRender ??
            (() => <ListNoData message={noDataMessage} loading={loading} />)
          }
          {...(allowCustom !== undefined ? { allowCustom } : {})}
          loading={loading}
          onChange={e => field.onChange(e.value)}
          onBlur={field.onBlur}
          value={value}
        />
        {showHint && !hintIcon && <Hint id={hintId}>{hint}</Hint>}
        {showHint && hintIcon && <IconHint hint={hint} style={{ right: 18 }} />}
        {showError && <Error id={errorId}>{fieldState.error?.message}</Error>}
      </FormLabel>
    </FieldWrapper>
  );
};

export default FormKendoMultiSelect;
